import { Link, useLocation } from "react-router-dom";
import { Menu, PanelLeftClose, PanelLeftOpen, CheckSquare, LogOut, User } from "lucide-react";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { findNav } from "@/lib/nav";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CompanyBranchSwitcher } from "./CompanyBranchSwitcher";
import { GlobalSearch } from "./GlobalSearch";
import { NotificationBell } from "./NotificationBell";

const Breadcrumbs = () => {
  const { pathname } = useLocation();
  const item = findNav(pathname);
  return (
    <div className="hidden md:block text-sm font-medium truncate" data-testid="breadcrumbs">
      <span data-testid="breadcrumb-current">{item?.label || "Garage39"}</span>
    </div>
  );
};

const ApprovalsButton = () => {
  const { can, company } = useSession();
  const { pathname } = useLocation();
  const ok = !!company && can("maintenance.view");
  const { data } = useFetch("/service-approvals", { status: "pending", limit: 1, _p: pathname }, ok);
  if (!ok) return null;
  const n = data?.total || 0;
  return (
    <Link to="/service-approvals" className="relative h-9 px-2.5 sm:px-3 rounded-md border bg-white flex items-center gap-1.5 text-sm hover:bg-secondary" data-testid="topbar-approvals-button" title="Approvals">
      <CheckSquare className="h-4 w-4 text-gold" />
      <span className="hidden sm:inline">Approvals</span>
      {n > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-gold text-[11px] font-semibold text-[#1F2328] flex items-center justify-center" data-testid="topbar-approvals-count">{n}</span>}
    </Link>
  );
};

export const Topbar = ({ collapsed, onToggle, onMobileMenu }) => {
  const { me, logout } = useSession();
  const initials = me?.user?.name
    ? me.user.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : "G39";

  return (
    <header className="sticky top-0 z-30 h-16 border-b bg-white flex items-center gap-2 sm:gap-3 px-4 sm:px-6" data-testid="topbar">
      <button className="lg:hidden h-9 w-9 rounded-md border bg-white flex items-center justify-center" onClick={onMobileMenu} data-testid="mobile-menu-button">
        <Menu className="h-4 w-4" />
      </button>
      <button className="hidden lg:flex h-9 w-9 rounded-md hover:bg-secondary items-center justify-center" onClick={onToggle} data-testid="sidebar-toggle-button">
        {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
      </button>
      <Breadcrumbs />
      <div className="flex-1" />
      <GlobalSearch />
      <ApprovalsButton />
      <CompanyBranchSwitcher />
      <NotificationBell />
      <div className="hidden xl:flex flex-col items-end leading-tight" data-testid="current-user-info">
        <span className="text-[13px] font-medium max-w-[140px] truncate">{me?.user?.name}</span>
        <span className="text-[10px] uppercase tracking-wider text-gold font-semibold" data-testid="current-user-role">{me?.role_label}</span>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="h-9 w-9 rounded-full bg-[#18191D] text-gold font-bold text-xs flex items-center justify-center hover:opacity-90 transition-opacity border border-gold/30"
            data-testid="user-profile-button"
            title={me?.user?.name || "Account"}
          >
            {initials}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 bg-white shadow-lg border">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-semibold leading-none">{me?.user?.name || "User"}</p>
              <p className="text-xs leading-none text-muted-foreground">{me?.user?.email}</p>
              <span className="text-[10px] uppercase font-semibold text-gold tracking-wider mt-1">{me?.role_label || me?.role}</span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer flex items-center" data-testid="logout-button">
            <LogOut className="mr-2 h-4 w-4" />
            <span>Sign out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
};
